import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Bot, Loader2, ChevronRight, ChevronLeft, Search } from "lucide-react";

const COLUMNS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "blocked", label: "Blocked" },
  { key: "done", label: "Done" },
];

function AddTaskModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/tasks", { title, assignee: assignee || null, due_date: dueDate || null });
      toast({ title: "Task added" });
      setTitle(""); setAssignee(""); setDueDate("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add task", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-task-modal">
        <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-task-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assignee</Label>
              <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="e.g. Priya Nair" className="mt-1" data-testid="compliance-task-assignee-input" />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="compliance-task-due-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-task-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-task-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceTasks({ isAdmin }) {
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/tasks");
      setTasks(data.tasks || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (task, dir) => {
    const idx = COLUMNS.findIndex((c) => c.key === task.status);
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= COLUMNS.length) return;
    const status = COLUMNS[nextIdx].key;
    try {
      await api.put(`/compliance/tasks/${task.id}`, { status });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    } catch (e) {
      toast({ title: "Couldn't move task", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  if (loading) return <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-20 rounded-xl bg-muted/40 animate-pulse" />)}</div>;

  const visibleTasks = search
    ? tasks.filter((t) => `${t.title} ${t.assignee || ""}`.toLowerCase().includes(search.toLowerCase()))
    : tasks;

  return (
    <div data-testid="compliance-tasks-board">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" className="pl-8 h-9" data-testid="compliance-tasks-search" />
        </div>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => setModalOpen(true)} data-testid="compliance-task-add-btn">
            <Plus className="w-3.5 h-3.5" /> New task
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {COLUMNS.map((col, colIdx) => {
          const colTasks = visibleTasks.filter((t) => t.status === col.key);
          return (
            <div key={col.key} className="rounded-xl border border-border bg-card/50 p-3" data-testid={`compliance-task-column-${col.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{col.label}</div>
                <span className="text-[11px] rounded-full bg-muted px-1.5 py-0.5 font-semibold">{colTasks.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {colTasks.map((t) => (
                  <div key={t.id} className="rounded-lg border border-border bg-card p-2.5" data-testid={`compliance-task-card-${t.id}`}>
                    <div className="text-[12.5px] font-medium leading-snug flex items-start gap-1.5">
                      {t.origin === "auto" && <Bot className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />}
                      <span>{t.title}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center justify-between">
                      <span>{t.assignee || "Unassigned"}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
                      {isAdmin && (
                        <div className="flex items-center gap-0.5">
                          <button type="button" disabled={colIdx === 0} onClick={() => move(t, -1)} data-testid={`compliance-task-prev-${t.id}`} className={cn("p-0.5 rounded hover:bg-muted", colIdx === 0 && "opacity-30 cursor-not-allowed")}><ChevronLeft className="w-3.5 h-3.5" /></button>
                          <button type="button" disabled={colIdx === COLUMNS.length - 1} onClick={() => move(t, 1)} data-testid={`compliance-task-next-${t.id}`} className={cn("p-0.5 rounded hover:bg-muted", colIdx === COLUMNS.length - 1 && "opacity-30 cursor-not-allowed")}><ChevronRight className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-4">{search ? "No matches" : "No tasks"}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <AddTaskModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
    </div>
  );
}
